import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar as CalendarIcon, Clock, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Phone, Mail, User, Building2, MessageSquare, ShieldCheck, Globe } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const humanDate = (d) =>
  d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const isClosed = (d) => d.getDay() === 0; // Sunday

const buildMonthGrid = (year, month) => {
  // Returns an array of dates for the month, padded so Monday is the first column.
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Mon=0...Sun=6
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export const BookCallModal = ({ open, onOpenChange, context = null, market = 'uk' }) => {
  const [step, setStep] = useState(1); // 1=date+time · 2=details · 3=confirmation
  const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', business: '', website: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // Reset on close. Pre-fill the message when there's interest context (eg. package).
  useEffect(() => {
    if (!open) {
      setStep(1);
      setSelectedDate(null);
      setSelectedTime(null);
      setSlots([]);
      setForm({ name: '', email: '', phone: '', business: '', website: '', message: '' });
      setViewYear(today.getFullYear());
      setViewMonth(today.getMonth());
    } else if (context) {
      setForm((f) => ({
        ...f,
        message: f.message ? f.message : `Interested in: ${context}`,
      }));
    }
  }, [open, today, context]);

  // Fetch availability whenever the selected date changes
  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    setSelectedTime(null);
    fetch(`${API_URL}/api/bookings/availability?date=${formatYMD(selectedDate)}`)
      .then((r) => r.json())
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate]);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const submit = async () => {
    if (!selectedDate || !selectedTime || !form.name.trim() || !form.email.trim()) {
      toast.error('Please fill in your name + email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/bookings/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          date: formatYMD(selectedDate),
          time: selectedTime,
          source: 'site',
          market,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not save the booking. Try again.');
      }
      setStep(3);
    } catch (e) {
      toast.error(e.message || 'Booking failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => onOpenChange(false)}
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-zinc-950/70 backdrop-blur-sm p-4"
          data-testid="book-call-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-3xl max-w-4xl w-full max-h-[94vh] overflow-hidden flex flex-col shadow-[0_24px_72px_rgba(0,0,0,0.3)]"
          >
            {/* Header */}
            <div className="px-6 md:px-8 pt-6 pb-4 border-b border-zinc-100 flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-blue-600 mb-1">Book A Call</p>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-zinc-950 tracking-tight">
                  {step === 1 && 'Pick a time that works for you'}
                  {step === 2 && 'Tell us a bit about you'}
                  {step === 3 && "You're booked in"}
                </h2>
              </div>
              <button onClick={() => onOpenChange(false)} className="p-2 text-zinc-400 hover:text-zinc-950 transition-colors rounded-full hover:bg-zinc-100 shrink-0" data-testid="book-call-close">
                <X size={18} />
              </button>
            </div>

            {/* Step progress */}
            {step < 3 && (
              <div className="px-6 md:px-8 py-3 bg-zinc-50/50 flex items-center gap-2 text-xs">
                <div className={`flex items-center gap-1.5 font-bold ${step === 1 ? 'text-blue-600' : 'text-emerald-600'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 1 ? 'bg-blue-100' : 'bg-emerald-100'}`}>1</span>
                  Date & time
                </div>
                <div className="flex-1 h-px bg-zinc-200" />
                <div className={`flex items-center gap-1.5 font-bold ${step === 2 ? 'text-blue-600' : 'text-zinc-400'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step === 2 ? 'bg-blue-100' : 'bg-zinc-100'}`}>2</span>
                  Your details
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6">
              {/* STEP 1 — Calendar + time picker (side-by-side on desktop, stacked mobile) */}
              {step === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-6 md:gap-8" data-testid="book-call-step-1">
                  {/* Calendar */}
                  <div className="space-y-4">
                    {/* Month nav */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear-1); } else setViewMonth(viewMonth-1); }}
                        disabled={viewYear === today.getFullYear() && viewMonth <= today.getMonth()}
                        className="p-2 rounded-full text-zinc-700 hover:bg-zinc-100 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="cal-prev"
                      >
                        <ArrowLeft size={16} />
                      </button>
                      <p className="text-sm font-bold text-zinc-950">{monthLabel}</p>
                      <button
                        onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear+1); } else setViewMonth(viewMonth+1); }}
                        className="p-2 rounded-full text-zinc-700 hover:bg-zinc-100"
                        data-testid="cal-next"
                      >
                        <ArrowRight size={16} />
                      </button>
                    </div>

                    {/* Calendar grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((d) => (
                        <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wider text-zinc-400 py-1">{d}</div>
                      ))}
                      {grid.map((d, i) => {
                        if (!d) return <div key={i} />;
                        const past = d < today;
                        const closed = isClosed(d);
                        const sel = selectedDate && formatYMD(d) === formatYMD(selectedDate);
                        const isToday = formatYMD(d) === formatYMD(today);
                        return (
                          <button
                            key={i}
                            disabled={past || closed}
                            onClick={() => setSelectedDate(d)}
                            data-testid={`cal-day-${formatYMD(d)}`}
                            className={`aspect-square rounded-lg text-sm font-semibold transition-all flex items-center justify-center ${
                              sel
                                ? 'bg-zinc-950 text-white shadow-md scale-105'
                                : past || closed
                                ? 'text-zinc-300 cursor-not-allowed'
                                : isToday
                                ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 ring-1 ring-blue-200'
                                : 'text-zinc-900 hover:bg-zinc-100'
                            }`}
                          >
                            {d.getDate()}
                          </button>
                        );
                      })}
                    </div>

                    <p className="text-[11px] text-zinc-400 font-medium">All times shown in {market === 'in' ? 'India Standard Time (IST)' : 'UK time'} · 15-30 minute call · No pitch deck.</p>
                  </div>

                  {/* Time slots — column right on desktop, full-width below mobile */}
                  <div className="md:border-l md:border-zinc-100 md:pl-6">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
                      <Clock size={11} />
                      {selectedDate ? humanDate(selectedDate).replace(/, \d{4}$/, '') : 'Pick a date first'}
                    </p>
                    {!selectedDate ? (
                      <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-8 text-center bg-zinc-50/40" data-testid="slots-empty-state">
                        <CalendarIcon size={20} className="text-zinc-300 mx-auto mb-2" />
                        <p className="text-xs text-zinc-400 font-medium leading-relaxed">Tap a day on the calendar to see the times available.</p>
                      </div>
                    ) : slotsLoading ? (
                      <p className="text-sm text-zinc-500 flex items-center gap-2 py-4"><Loader2 size={14} className="animate-spin" />Loading slots…</p>
                    ) : slots.length === 0 ? (
                      <p className="text-sm text-zinc-500 py-4">No slots free that day — try another date.</p>
                    ) : (
                      <div className="grid grid-cols-3 md:grid-cols-2 gap-1.5 max-h-[280px] md:max-h-[340px] overflow-y-auto pr-1" data-testid="slot-list">
                        {slots.map((t) => (
                          <button
                            key={t}
                            onClick={() => setSelectedTime(t)}
                            data-testid={`slot-${t}`}
                            className={`px-3 py-2 rounded-lg text-sm font-bold transition-all ${
                              selectedTime === t
                                ? 'bg-zinc-950 text-white shadow-md'
                                : 'bg-white border border-zinc-200 text-zinc-900 hover:border-zinc-400 hover:bg-zinc-50'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* STEP 2 — Details */}
              {step === 2 && (
                <div className="space-y-4" data-testid="book-call-step-2">
                  <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center gap-3">
                    <CalendarIcon size={16} className="text-blue-600 shrink-0" />
                    <p className="text-sm font-semibold text-blue-900">{humanDate(selectedDate)} at {selectedTime}</p>
                  </div>
                  {[
                    { name: 'name', icon: User, label: 'Your name', placeholder: 'Mark Roberts', required: true },
                    { name: 'email', icon: Mail, label: 'Email', placeholder: 'mark@markselectrical.co.uk', required: true, type: 'email' },
                    { name: 'phone', icon: Phone, label: 'Phone (so I can call)', placeholder: '07900 123 456' },
                    { name: 'business', icon: Building2, label: 'Business name', placeholder: 'Marks Electrical' },
                    { name: 'website', icon: Globe, label: 'Website (if you have one)', placeholder: 'markselectrical.co.uk', type: 'url' },
                  ].map((f) => {
                    const Icon = f.icon;
                    return (
                      <div key={f.name}>
                        <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
                          <Icon size={11} />{f.label}{f.required && <span className="text-rose-500">*</span>}
                        </label>
                        <input
                          type={f.type || 'text'}
                          value={form[f.name]}
                          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                          placeholder={f.placeholder}
                          data-testid={`book-call-input-${f.name}`}
                          className="w-full bg-white border border-zinc-200 focus:border-zinc-950 rounded-xl px-4 py-3 text-sm text-zinc-950 placeholder:text-zinc-400 outline-none transition-colors"
                        />
                      </div>
                    );
                  })}
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare size={11} />What's on your mind? (optional)
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={3}
                      placeholder="One line on what you're hoping to fix or improve."
                      data-testid="book-call-input-message"
                      className="w-full bg-white border border-zinc-200 focus:border-zinc-950 rounded-xl px-4 py-3 text-sm text-zinc-950 placeholder:text-zinc-400 outline-none transition-colors resize-none"
                    />
                  </div>
                </div>
              )}

              {/* STEP 3 — Confirmation */}
              {step === 3 && (
                <div className="text-center py-6" data-testid="book-call-step-3">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center mx-auto mb-5 shadow-[0_8px_24px_rgba(16,185,129,0.35)]">
                    <CheckCircle2 size={28} className="text-white" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-zinc-950 mb-2">You're booked in.</h3>
                  <p className="text-sm text-zinc-500 mb-5 max-w-sm mx-auto">
                    I'll give you a ring on <strong className="text-zinc-950">{humanDate(selectedDate)}</strong> at <strong className="text-zinc-950">{selectedTime}</strong>.
                  </p>
                  <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 inline-flex items-center gap-2 text-sm text-emerald-800 font-semibold">
                    <Mail size={14} />Confirmation email sent to <strong>{form.email}</strong>
                  </div>
                  <div className="mt-7 flex items-center justify-center gap-2 text-xs text-zinc-400">
                    <ShieldCheck size={12} className="text-emerald-500" />
                    Need to reschedule? Reply to the email or call 01603 976281.
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 md:px-8 py-4 border-t border-zinc-100 bg-zinc-50/50 flex items-center justify-between gap-3">
              {step === 1 && (
                <>
                  <p className="text-xs text-zinc-400 flex items-center gap-1.5"><Clock size={11} />15-30 min · No pitch deck</p>
                  <button
                    onClick={() => setStep(2)}
                    disabled={!selectedDate || !selectedTime}
                    data-testid="book-call-next"
                    className="bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 rounded-full font-bold text-sm flex items-center gap-2 transition-colors"
                  >
                    Next <ArrowRight size={14} />
                  </button>
                </>
              )}
              {step === 2 && (
                <>
                  <button onClick={() => setStep(1)} className="text-sm font-bold text-zinc-600 hover:text-zinc-950 flex items-center gap-1.5" data-testid="book-call-back">
                    <ArrowLeft size={13} />Back
                  </button>
                  <button
                    onClick={submit}
                    disabled={submitting || !form.name.trim() || !form.email.trim()}
                    data-testid="book-call-submit"
                    className="bg-gradient-to-r from-emerald-500 to-blue-600 text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed px-6 py-3 rounded-full font-bold text-sm flex items-center gap-2 transition-opacity"
                  >
                    {submitting ? <><Loader2 size={14} className="animate-spin" />Booking…</> : <><CheckCircle2 size={14} />Confirm booking</>}
                  </button>
                </>
              )}
              {step === 3 && (
                <button onClick={() => onOpenChange(false)} className="ml-auto bg-zinc-950 text-white hover:bg-zinc-800 px-6 py-3 rounded-full font-bold text-sm" data-testid="book-call-done">
                  Done
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
